import { useInfiniteQuery } from "@tanstack/react-query";

import { fetchDistributions } from "@/hooks/yaas/useDistributions";
import { type HistoryEntry, mergeHistory } from "@/services/transactions/mergeHistory";
import { fetchUsdvSwaps, getHistoryPreviewState, USDV_SWAP_POOL } from "@/services/transactions/usdvSwaps";

const DISTRIBUTIONS_PAGE_SIZE = 20;

export interface AccountHistoryResult {
    entries: HistoryEntry[];
    isLoading: boolean;
    isFetching: boolean;
    hasMore: boolean;
    loadMore: () => Promise<void>;
    retry: () => Promise<void>;
    swapsError: boolean;
    distributionsError: boolean;
    isEmpty: boolean;
}

export function useAccountTransactionHistory(
    walletAddress: string,
    balanceAddress = walletAddress
): AccountHistoryResult {
    const demo = getHistoryPreviewState();
    const swaps = useInfiniteQuery({
        queryKey: ["transactionHistory", balanceAddress, USDV_SWAP_POOL, { demo }],
        initialPageParam: undefined as string | undefined,
        queryFn: ({ pageParam, signal }) => fetchUsdvSwaps(balanceAddress, pageParam, signal),
        getNextPageParam: (page) => page.paginationToken ?? undefined,
        enabled: !!balanceAddress,
        staleTime: 60_000,
        retry: 1
    });
    const distributions = useInfiniteQuery({
        queryKey: ["yaas", "distributions", walletAddress, "history", { demo }],
        initialPageParam: 1,
        queryFn: async ({ pageParam, signal }) => {
            try {
                return await fetchDistributions(walletAddress, pageParam, DISTRIBUTIONS_PAGE_SIZE, signal);
            } catch (error) {
                // A wallet can trade without enrolling in rewards.
                if (error instanceof Error && error.message === "NOT_WHITELISTED") {
                    return { data: [], total: 0, limit: DISTRIBUTIONS_PAGE_SIZE, page: pageParam };
                }
                throw error;
            }
        },
        getNextPageParam: (page) => (page.page * page.limit < page.total ? page.page + 1 : undefined),
        enabled: !!walletAddress,
        staleTime: 60_000,
        retry: 1
    });
    const swapPages = swaps.data?.pages ?? [];
    const distributionPages = distributions.data?.pages ?? [];
    const swapRows = swapPages.flatMap((page) => page.transactions);
    const distributionRows = distributionPages.flatMap((page) => page.data);
    // Only reveal the range scanned by BOTH sources. Otherwise loading the
    // next page can insert newer rows ahead of already-visible older rows.
    const swapCutoff = swaps.hasNextPage ? Math.min(...swapPages.map((page) => page.oldestTimestamp)) : -Infinity;
    const distributionCutoff = distributions.hasNextPage
        ? Math.min(...distributionRows.map((row) => Date.parse(row.distributionDate) / 1000 || 0))
        : -Infinity;
    const hasError = swaps.isError || distributions.isError;
    const cutoff = hasError ? -Infinity : Math.max(swapCutoff, distributionCutoff);
    const entries = mergeHistory(swapRows, distributionRows, cutoff);
    const isLoading = !!walletAddress && (swaps.isPending || distributions.isPending);
    const isFetching = swaps.isFetching || distributions.isFetching;
    const hasMore = swaps.hasNextPage || distributions.hasNextPage;

    async function loadMore(): Promise<void> {
        if (isFetching) return;
        // Advance the source(s) at the frontier; retain already-fetched older
        // rows from the other source until their chronological range is reached.
        await Promise.all([
            swaps.hasNextPage && (hasError || swapCutoff >= distributionCutoff) ? swaps.fetchNextPage() : undefined,
            distributions.hasNextPage && (hasError || distributionCutoff >= swapCutoff)
                ? distributions.fetchNextPage()
                : undefined
        ]);
    }

    async function retry(): Promise<void> {
        await Promise.all([
            swaps.isError ? (swaps.isFetchNextPageError ? swaps.fetchNextPage() : swaps.refetch()) : undefined,
            distributions.isError
                ? distributions.isFetchNextPageError
                    ? distributions.fetchNextPage()
                    : distributions.refetch()
                : undefined
        ]);
    }

    return {
        entries,
        isLoading,
        isFetching,
        hasMore,
        loadMore,
        retry,
        swapsError: swaps.isError,
        distributionsError: distributions.isError,
        isEmpty: !isLoading && !hasError && !hasMore && entries.length === 0
    };
}
